import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { FileText, MessageSquare, CheckSquare, ClipboardList, ShoppingCart, Receipt } from 'lucide-react';
import { fetchRecentActivity, ActivityItem } from '@/services/activityService';
import { useUser } from '@/contexts/UserContext';
import { useProject } from '@/components/ProjectSelector';
import '@/design-system/typography.css';
import '@/design-system/forms.css';

export function RecentActivity() {
  const { companyId } = useUser();
  const { selectedProject } = useProject();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadActivities = async () => {
      if (!companyId) return;
      
      setIsLoading(true);
      try {
        const data = await fetchRecentActivity(selectedProject, 4);
        setActivities(data);
      } catch (error) {
        console.error('Error loading activities:', error);
        setActivities([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadActivities();
  }, [companyId, selectedProject]);

  const getIcon = (type: ActivityItem['type']) => {
    switch (type) {
      case 'task':
        return <CheckSquare className="h-5 w-5 text-gray-600" />;
      case 'daily_log':
        return <ClipboardList className="h-5 w-5 text-gray-600" />;
      case 'purchase_order':
        return <ShoppingCart className="h-5 w-5 text-gray-600" />;
      case 'bill':
        return <Receipt className="h-5 w-5 text-gray-600" />;
      case 'comment':
        return <MessageSquare className="h-5 w-5 text-gray-600" />;
      default:
        return <FileText className="h-5 w-5 text-gray-600" />;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <h2 className="font-section-subheader mb-4">Recent Activity</h2>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-start space-x-4">
                <div className="rounded-full bg-gray-100 p-2 animate-pulse">
                  <div className="h-5 w-5 bg-gray-300 rounded" />
                </div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-300 rounded animate-pulse mb-2" />
                  <div className="h-3 bg-gray-200 rounded animate-pulse mb-1" />
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-1/3" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="font-section-subheader mb-4">Recent Activity</h2>
        <div className="space-y-4">
          {activities.length === 0 ? (
            <div className="text-center py-4 font-dashboard-text-muted">
              No recent activity
            </div>
          ) : (
            activities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-4">
                <div className="rounded-full bg-gray-100 p-2">
                  {getIcon(activity.type)}
                </div>
                <div>
                  <p className="font-subheading-text text-gray-900">{activity.title}</p>
                  <p className="font-dashboard-text-secondary">{activity.description}</p>
                  <p className="font-dashboard-text-muted mt-1">{activity.timestamp}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
} 